// A passage page (PRD P7): every reference in Fellowship opens here. The text, a way into the full
// reader in Bible Standard, and what friends have said about this passage, so a verse becomes a
// place people meet rather than a line that scrolls past.
import { createFileRoute, Link } from "@tanstack/react-router";
import { Empty } from "../../components/Empty";
import { Icon } from "../../components/Icon";
import { Post } from "../../components/Post";
import ui from "../../components/ui.module.css";
import { parsePassage } from "../../data/books";
import { usePassage, usePassagePosts } from "../../data/queries";
import { useTitle } from "../../lib/useTitle";
import s from "./passage.module.css";

export const Route = createFileRoute("/_app/r/$ref")({ component: PassagePage });

function PassagePage() {
  const { ref } = Route.useParams();
  // Verses or a whole chapter ("Rom.8.28", "Romans 8"), from the World English Bible.
  const parsed = parsePassage(ref);
  const { data: passage, isPending } = usePassage(ref);
  const { data: posts } = usePassagePosts(ref);
  const label = passage?.label ?? parsed?.label ?? ref;
  useTitle(label);

  return (
    <div className={ui.page}>
      <header className={s.head}>
        <h1 className={s.title}>{label}</h1>
        {passage && <p className={s.translation}>World English Bible · public domain</p>}
      </header>

      {isPending ? (
        <div className={s.text} aria-busy="true" />
      ) : passage?.chapters ? (
        // A chapter or more (a reading plan's day): read verse by verse, at reading size.
        <div className={s.reading}>
          {passage.chapters.map((c) => (
            <section key={c.n} aria-label={passage.chapters!.length > 1 ? `Chapter ${c.n}` : undefined}>
              {passage.chapters!.length > 1 && <h2 className={s.chapter}>Chapter {c.n}</h2>}
              <p>
                {c.verses.map((v) => (
                  <span key={v.n}>
                    <sup className={s.verse}>{v.n}</sup>
                    {v.text}{" "}
                  </span>
                ))}
              </p>
            </section>
          ))}
        </div>
      ) : passage ? (
        <blockquote className={s.text}>{passage.text}</blockquote>
      ) : (
        <p className={s.missing}>
          {parsed ? "We can’t load this passage just now. Open it in Bible Standard to read it." : "We can’t find that passage."}
        </p>
      )}

      <div className={s.actions}>
        {passage && parsed && parsed.chapterEnd === parsed.chapter && (
          <Link to="/new" search={{ kind: "verse", ref: parsed?.from ? label : undefined }} className={ui.primary}>
            <Icon name="compose" size={18} />
            Share this passage
          </Link>
        )}
        {parsed && (
          <a className={ui.secondary} href={`https://biblestandard.app/r/${encodeURIComponent(parsed.key)}`}>
            <Icon name="read" size={18} />
            Open in Bible Standard
          </a>
        )}
      </div>

      <section className={s.friends} aria-labelledby="said-head">
        <h2 id="said-head" className={ui.sectionTitle}>
          What friends said
        </h2>
        {posts && posts.length > 0 ? (
          posts.map((p) => <Post key={p.id} post={p} />)
        ) : (
          <Empty title="None of your friends has shared this passage yet." />
        )}
      </section>
    </div>
  );
}
